// The interactive HTML export: one standalone .html file that embeds the
// prebuilt report viewer (the SITE's own React output components – recharts,
// drag/lasso selection, employee-group dialogs, custom charts) plus the
// current state as JSON. Nothing about the charts or interactions is
// re-implemented: the exported file runs the same code the website runs.
//
// assembleViewerHtml / hiddenIdsFor are pure (node-testable). The generate*
// entries are browser-only: they read the stores and fetch the viewer bundle
// from /report-viewer/ (built by `npm run build:viewer`, served from public/).

import { isSectionEnabled, resolveReportMeta, resolveReportBrand } from '../reportConfig.js'
import { REPORT_PLAN_SLOTS } from '../../export/reportState.js'

// Keep only the plans currently shown in the workspace. A standalone report
// may be client-facing, so a plan hidden in the header must not quietly return
// in its chart picker or payload. The snapshot remains self-contained and
// cannot write back to the app.
export function buildInteractiveReportState(state) {
  const hidden = new Set(Array.isArray(state.hiddenPlanKeys) ? state.hiddenPlanKeys : [])
  const plans = Object.fromEntries(REPORT_PLAN_SLOTS.map((key) => [
    key,
    hidden.has(key) ? null : (state[key] || null),
  ]))
  return {
    employees: state.employees,
    plans,
    activePlanKey: 'planA',
    customCharts: state.customCharts || [],
    reportConfig: state.reportConfig || {},
    censusLocal: state.censusLocal,
    workspaceMode: state.workspaceMode,
  }
}

// The site's chart library (Visualizations.jsx), as groups + the individual
// charts inside each. Shared by both exports: the standard report exposes
// each group as its own top-level toggle, while the CFO brief nests this same
// tree under its single "Full chart library" toggle – either way the DOM ids
// are the site's real ones, so hiding is pure CSS and nothing re-renders.
const VISUALIZATION_CHART_GROUPS = [
  {
    key: 'chartsOverview', label: 'Overview charts', id: 'visualizations-overview',
    children: [
      { key: 'chartParticipationStatus', label: 'Participation status', id: 'chart-participation-status' },
      { key: 'chartHceNhceComparison', label: 'HCE vs NHCE comparison', id: 'chart-hce-nhce-comparison' },
      { key: 'chartOverviewKpiBoxes', label: 'Overview KPI stats', id: 'chart-overview-kpi-boxes' },
    ],
  },
  {
    key: 'chartsContributions', label: 'Contribution charts', id: 'visualizations-contributions',
    children: [
      { key: 'chartAgeContributions', label: 'Employee contributions by age band', id: 'chart-age-contributions' },
      { key: 'chartEmployerByAgeBand', label: 'Employer contributions by age band', id: 'chart-employer-by-age-band' },
    ],
  },
  {
    key: 'chartsDistributions', label: 'Distribution charts', id: 'visualizations-distributions',
    children: [
      { key: 'chartDeferralDistribution', label: 'Deferral rate distribution', id: 'chart-deferral-distribution' },
      { key: 'chartCompensationDistribution', label: 'Compensation distribution', id: 'chart-compensation-distribution' },
      { key: 'chartCatchupUsage', label: 'Catch-up usage', id: 'chart-catchup-usage' },
    ],
  },
  {
    key: 'chartsRelationships', label: 'Relationship charts', id: 'visualizations-relationships',
    children: [
      { key: 'chartCompVsDeferral', label: 'Compensation vs deferral rate', id: 'chart-comp-vs-deferral' },
      { key: 'chartAgeVsContribution', label: 'Age vs total contribution', id: 'chart-age-vs-contribution' },
    ],
  },
  {
    key: 'chartsTurnover', label: 'Turnover', id: 'visualizations-turnover',
    children: [
      { key: 'chartHeadcountMovement', label: 'Headcount movement', id: 'chart-headcount-movement' },
      { key: 'chartForfeitures', label: 'Forfeitures from turnover', id: 'chart-forfeitures' },
      { key: 'chartShortTermTerminations', label: 'Short-term terminations', id: 'chart-short-term-terminations' },
    ],
  },
  {
    key: 'chartsDemographics', label: 'Demographics', id: 'visualizations-demographics',
    children: [
      { key: 'chartGenderPayGap', label: 'Gender pay gap analysis', id: 'chart-gender-pay-gap' },
      { key: 'chartAgeDeferralHeatmap', label: 'Average deferral by age and gender', id: 'chart-age-deferral-heatmap' },
      { key: 'chartAgeDistribution', label: 'Age distribution', id: 'chart-age-distribution' },
      { key: 'chartTenureDistribution', label: 'Tenure distribution', id: 'chart-tenure-distribution' },
    ],
  },
  {
    key: 'chartsLoans', label: 'Loans', id: 'visualizations-loans',
    children: [
      { key: 'chartLoanTakersByAge', label: "Who's taking loans", id: 'chart-loan-takers-by-age' },
      { key: 'chartLoanAmountByAge', label: 'Loan amount by age band', id: 'chart-loan-amount-by-age' },
      { key: 'chartLoanVsBalance', label: 'Loan vs balance', id: 'chart-loan-vs-balance' },
      { key: 'chartLoanDistribution', label: 'Loan distribution', id: 'chart-loan-distribution' },
    ],
  },
  { key: 'chartsCustom', label: 'Custom charts (your saved charts)', id: 'visualizations-custom', children: [] },
]

// Flattens the chart-group inventory to `{key,label,id,parent}` rows. Pass
// `rootParent` to nest the whole tree one level deeper under an existing key
// (the CFO brief's "Full chart library"); omit it for the standard report,
// where each group is already a top-level toggle.
function flattenChartGroups(rootParent) {
  return VISUALIZATION_CHART_GROUPS.flatMap((group) => [
    { key: group.key, label: group.label, id: group.id, ...(rootParent ? { parent: rootParent } : {}) },
    ...group.children.map((child) => ({ ...child, parent: group.key })),
  ])
}

// Sections a user can include in the standard interactive report, organized in
// the same client reading order as PDF. Every leaf maps to a real DOM id in the
// exported page, so hiding is pure CSS and the components render exactly as
// they do in the app.
//
// Modeled compliance screens intentionally stay in the in-app Checks stage for
// TPAs and other plan professionals. They are not included in downloadable HTML
// reports because those files may be sent to clients.
export const HTML_REPORT_SECTIONS = [
  { key: 'executiveSummary', label: 'Executive summary', id: 'report-chapter-executive-summary' },
  { key: 'glanceHeadline', label: 'Headline allocation', id: 'glance-headline', parent: 'executiveSummary' },
  { key: 'summaryKpis', label: 'Key results', id: 'summary-kpis', parent: 'executiveSummary' },

  { key: 'costAnalysis', label: 'Cost analysis', id: 'report-chapter-cost-analysis' },
  { key: 'glanceSources', label: 'Where the money comes from', id: 'glance-sources', parent: 'costAnalysis' },
  { key: 'summaryContributions', label: 'Contribution summary', id: 'summary-contributions', parent: 'costAnalysis' },

  { key: 'employeeImpact', label: 'Employee impact', id: 'report-chapter-employee-impact' },
  { key: 'glanceRecipients', label: 'Who gets it', id: 'glance-recipients', parent: 'employeeImpact' },
  { key: 'glanceLineByLine', label: 'What each employee gets', id: 'glance-line-by-line', parent: 'employeeImpact' },

  // Temporarily disabled for client-facing reports.
  // { key: 'compliance', label: 'Required-testing check', id: 'summary-compliance' },

  // Off by default, exactly as in the PDF: a workforce chart study is not
  // part of the plan proposal a client is handed.
  { key: 'charts', label: 'Workforce charts', id: 'report-charts', parent: 'employeeImpact', defaultOff: true },
  ...flattenChartGroups('charts'),
]

// The site's summary dashboard is a shell around the KPI and contribution
// blocks. With both dropped it would export as an empty frame,
// so it is hidden as well (it has no toggle of its own – nothing in it is left
// to include).
const SUMMARY_DASHBOARD_ID = 'summary-dashboard'
const SUMMARY_DASHBOARD_PARTS = ['summaryKpis', 'summaryContributions']

// Internal setup prompts that carry jump-to-input buttons only the app can act
// on. Every export drops them rather than offering a toggle: a client-facing
// file must never ask its reader to go finish the plan setup.
export const EXPORT_ALWAYS_HIDDEN_IDS = ['plan-readiness-summary']

// Sections of the CFO brief (CfoSummary blocks + the studio summary it now
// contains + the full chart library), in page order.
export const CFO_BRIEF_SECTIONS = [
  { key: 'headline', label: 'Headline numbers', id: 'cfo-headline' },
  { key: 'headlineEmployerCost', label: 'Annual employer cost', id: 'cfo-metric-employer-cost', parent: 'headline' },
  { key: 'headlinePerEmployee', label: 'Per employee', id: 'cfo-metric-per-employee', parent: 'headline' },
  { key: 'headlineMaxExposure', label: 'Max exposure', id: 'cfo-metric-max-exposure', parent: 'headline' },
  { key: 'headlineForfeitures', label: 'Expected forfeitures', id: 'cfo-metric-forfeitures', parent: 'headline' },
  { key: 'headlineNetCost', label: 'Estimated net cost', id: 'cfo-metric-net-cost', parent: 'headline' },
  { key: 'credits', label: 'SECURE 2.0 tax credits', id: 'cfo-credits' },
  { key: 'assumptions', label: 'Assumptions used', id: 'cfo-assumptions' },
  { key: 'projection', label: 'Five-year projection', id: 'cfo-projection' },
  { key: 'utilization', label: 'Match utilization', id: 'cfo-utilization' },
  { key: 'workforce', label: 'Workforce pyramid', id: 'cfo-workforce' },
  { key: 'concentration', label: 'Where employer dollars land', id: 'cfo-concentration' },
  { key: 'planSummary', label: 'Plan summary KPIs', id: 'summary-kpis' },
  { key: 'contributionSummary', label: 'Contribution summary', id: 'summary-contributions' },
  // Client-facing CFO briefs omit the in-app compliance indicators too.
  // { key: 'compliance', label: 'Compliance indicators', id: 'summary-compliance' },
  { key: 'charts', label: 'Full chart library', id: 'report-charts' },
  ...flattenChartGroups('charts'),
]

// DOM ids to hide for every section the config turned off, including the ids
// beneath it: a chapter and the blocks inside it do not have to be nested in
// the DOM (the KPI cards live inside the site's dashboard), so containment is
// never assumed.
export function hiddenIdsFor(config, registry) {
  const enabled = new Map()
  const isVisible = (section) => {
    if (enabled.has(section.key)) return enabled.get(section.key)
    // Guard a malformed/cyclic registry: a section is its own ancestor at worst.
    enabled.set(section.key, false)
    const parent = section.parent ? registry.find((entry) => entry.key === section.parent) : null
    const visible = isSectionEnabled(config, section.key, registry) && (!parent || isVisible(parent))
    enabled.set(section.key, visible)
    return visible
  }
  return registry.filter((section) => !isVisible(section)).map((section) => section.id)
}

// The standard report's hidden ids: the section picks, the always-hidden
// internal prompts, plus the dashboard shell once nothing inside it is included.
export function standardHiddenIds(config) {
  const hidden = [...hiddenIdsFor(config, HTML_REPORT_SECTIONS), ...EXPORT_ALWAYS_HIDDEN_IDS]
  const keptInDashboard = SUMMARY_DASHBOARD_PARTS.some((key) => {
    const section = HTML_REPORT_SECTIONS.find((entry) => entry.key === key)
    return section && !hidden.includes(section.id)
  })
  return keptInDashboard ? hidden : [...hidden, SUMMARY_DASHBOARD_ID]
}

// `</script>`/`</style>` inside inlined code or JSON would terminate their
// host tags; neutralize the close sequences (semantics-preserving in JS
// strings, and json uses unicode escapes).
const safeJson = (value) => JSON.stringify(value).replaceAll('<', '\\u003c')
const safeInlineJs = (code) => code.replaceAll('</script', '<\\/script')
const safeInlineCss = (code) => code.replaceAll('</style', '<\\/style')

export function assembleViewerHtml({ title, css, js, payload }) {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<meta name="robots" content="noindex"/>
<title>${String(title ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')}</title>
<style>${safeInlineCss(css)}</style>
</head>
<body>
<div id="root"></div>
<script>window.__RETIRA_REPORT__=${safeJson(payload)}</script>
<script>${safeInlineJs(js)}</script>
</body>
</html>`
}

// ------------------------------------------------------------ browser side --

async function fetchViewerAssets() {
  const [jsRes, cssRes] = await Promise.all([
    fetch('/report-viewer/viewer.js'),
    fetch('/report-viewer/viewer.css'),
  ])
  if (!jsRes.ok || !cssRes.ok) {
    throw new Error('The report viewer bundle is missing. Run `npm run build:viewer` (it runs automatically with dev/build).')
  }
  return { js: await jsRes.text(), css: await cssRes.text() }
}

// Same-origin image → data URL so the exported file shows the logo offline.
async function toDataUrl(url) {
  try {
    const response = await fetch(url)
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

const preparedDate = () => new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date())

async function download(html, filename) {
  const { downloadFile } = await import('../utils.js')
  downloadFile(html, filename, 'text/html')
}

// Standard interactive report: the current proposal layout for regular
// workflows. Detailed variants restore the full Summary Dashboard followed by
// every chart group, with no content hidden.
export async function createInteractiveReportArtifact(planYear, { detailed = false, detailedDemo = false } = {}) {
  const { default: useStore } = await import('../../stores/useStore.js')
  const state = useStore.getState()
  const planKey = 'planA'
  const plan = state[planKey]
  const analysis = state.analysis[planKey]
  if (!analysis?.metrics) {
    throw new Error('No data available. Add employees and run calculations first.')
  }
  const effectivePlanYear = plan.planYear ?? planYear
  const isEstimate = state.workspaceMode === 'estimate'
  const reportConfig = state.reportConfig || {}
  const meta = resolveReportMeta(reportConfig, { planName: plan.name })
  const brand = resolveReportBrand(reportConfig, `${window.location.origin}/logo.png`)
  const logoSrc = brand.logoUrl?.startsWith('data:') ? brand.logoUrl : await toDataUrl(brand.logoUrl)
  const { js, css } = await fetchViewerAssets()
  const fullDetail = detailed || detailedDemo

  const payload = {
    mode: detailedDemo ? 'demo-detailed' : fullDetail ? 'detailed' : 'standard',
    hiddenIds: fullDetail ? [] : standardHiddenIds(reportConfig),
    cover: {
      brandName: brand.name,
      logoSrc,
      heading: meta.clientName || plan.name || '401(k) plan review',
      subtitle: meta.title,
      metaLine: fullDetail
        ? `Plan year ${effectivePlanYear} · ${plan.name || 'Plan A'} · Prepared ${preparedDate()}${meta.preparedBy ? ` by ${meta.preparedBy}` : ''}`
        : `${plan.name || 'Plan A'} · Plan year ${effectivePlanYear} · Prepared ${preparedDate()}${meta.preparedBy ? ` by ${meta.preparedBy}` : ''}`,
      badge: isEstimate ? 'Estimate' : null,
      footer: detailedDemo
        ? `Results are calculated from the 100-employee Demo census and current plan settings under plan year ${effectivePlanYear} IRS limits. This report does not include formal compliance testing and is not legal, tax, or investment advice. Charts are fully interactive: hover for values, drag or lasso across a chart to open the employees behind it. Generated by ${brand.attribution}.`
        : `Figures are estimates built from the census and plan settings on file, under ${effectivePlanYear} IRS limits. This report does not include formal compliance testing and is not tax, legal, or investment advice.${isEstimate ? ' This report is built from an ESTIMATED census, not real employee data.' : ''} The charts are live: hover for values, or drag across one to see the employees behind it. Prepared with ${brand.attribution}.`,
    },
    state: buildInteractiveReportState(state),
  }

  const html = assembleViewerHtml({
    title: `${payload.cover.heading}: 401(k) report`,
    css,
    js,
    payload,
  })
  const fileDate = new Date().toISOString().split('T')[0]
  const planName = (plan.name || 'Plan A').toLowerCase().replace(/\s+/g, '-')
  return {
    html,
    fileName: `retira-401k-${effectivePlanYear}-${planName}-${fileDate}${detailedDemo ? '-DEMO-DETAILED' : detailed ? '-DETAILED' : isEstimate ? '-ESTIMATE' : ''}.html`,
    mimeType: 'text/html',
  }
}

export async function generateInteractiveReport(planYear, options = {}) {
  const { html, fileName } = await createInteractiveReportArtifact(planYear, options)
  await download(html, fileName)
}

export function generateDetailedInteractiveReport(planYear) {
  return generateInteractiveReport(planYear, { detailed: true })
}

export function generateDetailedDemoInteractiveReport(planYear) {
  return generateInteractiveReport(planYear, { detailedDemo: true })
}

// CFO brief: the CFO cost summary (plus, optionally, the chart library) for
// the active plan, with the CFO store's cover fields and assumptions.
export async function generateCfoBrief() {
  const [{ default: useStore }, { default: useCfoStore }] = await Promise.all([
    import('../../stores/useStore.js'),
    import('../../stores/useCfoStore.js'),
  ])
  const state = useStore.getState()
  const planKey = 'planA'
  const plan = state[planKey]
  const analysis = state.analysis[planKey]
  if (!analysis?.metrics) {
    throw new Error('No data available. Add employees and run calculations first.')
  }
  const cfo = useCfoStore.getState()
  const isEstimate = state.workspaceMode === 'estimate'
  const logoSrc = await toDataUrl(`${window.location.origin}/logo.png`)
  const { js, css } = await fetchViewerAssets()

  const payload = {
    mode: 'cfo',
    hiddenIds: [...hiddenIdsFor(cfo.report, CFO_BRIEF_SECTIONS), ...EXPORT_ALWAYS_HIDDEN_IDS],
    cover: {
      brandName: 'retira.app',
      logoSrc,
      heading: '401(k) cost brief',
      subtitle: cfo.report.companyName || '',
      metaLine: `Plan year ${plan.planYear} · Prepared ${preparedDate()}${cfo.report.preparedBy ? ` by ${cfo.report.preparedBy}` : ''}`,
      badge: isEstimate ? 'Modeled estimate' : null,
      footer: `${isEstimate
        ? 'Figures are a modeled estimate built from stated assumptions, not a projection of an actual plan, and are not tax or legal advice.'
        : 'Figures are computed from the census loaded in the browser and are planning estimates, not a valuation, filing, or tax advice.'} Charts are fully interactive: hover for values, drag or lasso across a chart to open the employees behind it. Generated by Retira.app.`,
    },
    state: {
      employees: state.employees,
      plan,
      customCharts: (state.customCharts || []).map((chart) => ({ ...chart, planKey: 'planA' })),
      workspaceMode: state.workspaceMode,
      cfo: { assumptions: cfo.assumptions },
    },
  }

  const html = assembleViewerHtml({
    title: `401(k) CFO brief${cfo.report.companyName ? `: ${cfo.report.companyName}` : ''}`,
    css,
    js,
    payload,
  })
  const fileDate = new Date().toISOString().split('T')[0]
  const company = (cfo.report.companyName || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  await download(html, `retira-cfo-brief${company ? `-${company}` : ''}-${fileDate}.html`)
}
